import { BusinessDocType, DashboardMenu, WorkflowEntityType } from '../../types';
import { backendServices } from '../../services';

const dynamicDashboardMenuNavItems: Array<{ menu: DashboardMenu; path: string; name: string }> = [
    { menu: DashboardMenu.Overview, path: '/BuyerPortal/dashboard/overview', name: 'Overview' },
    {
        menu: DashboardMenu.StraightThroughProcessing,
        path: '/BuyerPortal/dashboard/straight-through-processing',
        name: 'Straight-Through Processing',
    },
    { menu: DashboardMenu.Processing, path: '/BuyerPortal/dashboard/processing', name: 'Processing' },
    { menu: DashboardMenu.Invoices, path: '/BuyerPortal/dashboard/invoices', name: 'Invoices' },
    { menu: DashboardMenu.PurchaseOrders, path: '/BuyerPortal/dashboard/purchase-orders', name: 'Purchase Orders' },
    { menu: DashboardMenu.POAcknowledgements, path: '/BuyerPortal/dashboard/po-acknowledgements', name: 'POAs' },
    { menu: DashboardMenu.ASNs, path: '/BuyerPortal/dashboard/asns', name: 'ASNs' },
    { menu: DashboardMenu.Suppliers, path: '/BuyerPortal/dashboard/suppliers', name: 'Suppliers' },
    { menu: DashboardMenu.Spend, path: '/BuyerPortal/dashboard/spend', name: 'Spend' },
];

const getDashboardMenuNavItem = (menu: DashboardMenu) => {
    const navItem = dynamicDashboardMenuNavItems.find((item) => item.menu === menu);
    if (!navItem) {
        throw new Error(`No nav item found for dashboard menu: ${menu}`);
    }
    return navItem;
};

export const subNavPaths = {
    dashboard: dynamicDashboardMenuNavItems,
    transactions: [
        { path: '/BuyerPortal/transactions/asns', name: 'ASNs', docType: BusinessDocType.ASNGeneric },
        {
            path: '/BuyerPortal/transactions/inventory',
            name: 'Inventory Advices',
            docType: BusinessDocType.InventoryAdvice,
        },
        { path: '/BuyerPortal/transactions/invoices', name: 'Invoices', docType: BusinessDocType.Invoice },
        {
            path: '/BuyerPortal/transactions/acklns',
            name: 'PO Acknowledgments',
            docType: BusinessDocType.PurchaseOrderAcknowledgement,
        },
        { path: '/BuyerPortal/transactions/pos', name: 'Purchase Orders', docType: BusinessDocType.PurchaseOrder },
        {
            path: '/BuyerPortal/transactions/consumption',
            name: 'Consumption Advices',
            docType: BusinessDocType.ConsumptionAdvice,
        },
        {
            path: '/BuyerPortal/transactions/receiving',
            name: 'Receiving Advices',
            docType: BusinessDocType.ReceivingAdvice,
        },
        {
            path: '/BuyerPortal/transactions/sim-documents',
            name: 'SIM Documents',
            docType: BusinessDocType.SIMDocument,
        },
        {
            path: '/BuyerPortal/transactions/statements',
            name: 'Statements',
            docType: BusinessDocType.Statement,
        },
    ],
    'parking-lot': [
        { path: 'all-workflows', name: 'All Workflows', workflowTemplateUI: 'All Workflows' },
        { path: 'invoice-reconcile', name: 'Nonstock Invoices', workflowTemplateUI: 'Invoice Reconcile' },
        { path: 'counter-pos', name: 'Counter POS', workflowTemplateUI: 'Counter POs' },
        { path: 'invoice-coding', name: 'Invoice Coding', workflowTemplateUI: 'Invoice Coding' },
        { path: 'document-approval', name: 'Document Approval', workflowTemplateUI: 'Document Approval' },
        { path: 'non-inventory', name: 'GL Coding', workflowTemplateUI: 'Non-Inventory' },
        { path: 'non-inventory-release', name: 'GL Coding Release', workflowTemplateUI: 'Non-Inventory Rel.' },
        { path: 'parked-documents', name: 'Parked Documents', workflowTemplateUI: 'Parked Documents' },
        { path: 'invoice-approval', name: 'PO Line Nums', workflowTemplateUI: 'Invoice Approval' },
        {
            path: 'create',
            name: 'New Document',
            workflowTemplateUI: 'Document Approval',
            showLeftNavInParkingLot: false,
        },
    ],
    'dynamic-discounting': [
        { path: '/BuyerPortal/dynamic-discounting/new', name: 'Create Campaign' },
        { path: '/BuyerPortal/dynamic-discounting/reports', name: 'Reporting' },
        { path: '/BuyerPortal/dynamic-discounting/users', name: 'Manage Users' },
        { path: '/BuyerPortal/dynamic-discounting/settings', name: 'Settings' },
    ],
    documents: [
        { path: '/SupplierPortal/documents/asns', name: 'ASNs', docType: BusinessDocType.ASNGeneric },
        {
            path: '/SupplierPortal/documents/consumption-advices',
            name: 'Consumption Advices',
            docType: BusinessDocType.ConsumptionAdvice,
        },
        {
            path: '/SupplierPortal/documents/inventory-advices',
            name: 'Inventory Advices',
            docType: BusinessDocType.InventoryAdvice,
        },
        { path: '/SupplierPortal/documents/invoices', name: 'Invoices', docType: BusinessDocType.Invoice },
        {
            path: '/SupplierPortal/documents/purchase-orders',
            name: 'Purchase Orders',
            docType: BusinessDocType.PurchaseOrder,
        },
        {
            path: '/SupplierPortal/documents/po-acknowledgments',
            name: 'PO Acknowledgments',
            docType: BusinessDocType.PurchaseOrderAcknowledgement,
        },
        {
            path: '/SupplierPortal/documents/receiving-advices',
            name: 'Receiving Advices',
            docType: BusinessDocType.ReceivingAdvice,
        },
        {
            path: '/SupplierPortal/documents/sim-documents',
            name: 'SIM Documents',
            docType: BusinessDocType.SIMDocument,
        },
        {
            path: '/SupplierPortal/documents/statements',
            name: 'Statements',
            docType: BusinessDocType.Statement,
        },
    ],
};

export const parseBusinessDocumentVisibility = (menuSettings: any, docType: BusinessDocType) =>
    !!menuSettings?.BusinessDocEnables?.find((item: any) => item.BusinessDocType === docType);

function getParkingLotMenuItems({
    currentCompany,
    menuSettings: { ChildCompanies, WorkflowsEnabled },
    includeSectionHeaders,
}: {
    currentCompany?: backendServices.ViewModels.CompanyViewModel;
    menuSettings: Pick<backendServices.ViewModels.BuyerPortalSettingsViewModel, 'WorkflowsEnabled' | 'ChildCompanies'>;
    includeSectionHeaders: boolean;
}) {
    const allCompanies: { name: string; id: number }[] =
        ChildCompanies?.map((c) => ({
            name: c.Name,
            id: c.Id,
        })) ?? [];

    if (currentCompany) {
        allCompanies.unshift({
            name: currentCompany.Name,
            id: currentCompany.ID,
        });
    }

    allCompanies.sort((a, b) => a.name.localeCompare(b.name));

    const multiCompanyIsAvailable = (ChildCompanies?.length ?? 0) > 0;

    const documentWorkflows = WorkflowsEnabled.filter(
        (workflow) => workflow.EntityType === WorkflowEntityType.Document
    );

    const sortedWorkflows = documentWorkflows.sort((a, b) => a.ShortName.localeCompare(b.ShortName));

    let workflowsSortedByCompany: Array<
        | typeof sortedWorkflows[number]
        | {
              isSectionHeader: true;
              label: string;
              companyId: number;
          }
    >[] = [];

    if (multiCompanyIsAvailable) {
        const workflowMap = new Map<number, typeof sortedWorkflows>();

        sortedWorkflows.forEach((workflow) => {
            const key = workflow.CompanyId;

            const registeredWorkflows = workflowMap.get(key);

            if (!registeredWorkflows) {
                workflowMap.set(key, [workflow]);
                return;
            }

            registeredWorkflows.push(workflow);
            workflowMap.set(key, registeredWorkflows);
        });

        workflowsSortedByCompany = allCompanies.map((c) => {
            const companyWorkflows = workflowMap.get(c.id);

            if (!companyWorkflows) {
                return [];
            }

            if (includeSectionHeaders) {
                return [
                    {
                        isSectionHeader: true,
                        label: c.name,
                        companyId: c.id,
                    },
                    ...companyWorkflows,
                ];
            }

            return companyWorkflows;
        });
    }

    let totalAcrossCompanies = 0;

    const itemGroups = (
        includeSectionHeaders && allCompanies.length > 1 ? workflowsSortedByCompany : [sortedWorkflows]
    ).map((workflowGroup) =>
        workflowGroup
            .map((item) => {
                if ('isSectionHeader' in item && item.isSectionHeader) {
                    return {
                        name: item.label,
                        count: 0,
                        isSectionHeader: true,
                        companyId: item.companyId,
                        path: '',
                    };
                }

                if (!('WorkflowTemplateUI' in item) || !item.WorkflowTemplateUI) {
                    return undefined;
                }

                const path = subNavPaths['parking-lot'].find(
                    (subItem) => subItem.workflowTemplateUI === item.WorkflowTemplateUI
                );

                totalAcrossCompanies += item.DocumentCount;

                let companyName: string | undefined = undefined;
                if (multiCompanyIsAvailable && item.CompanyId) {
                    companyName = allCompanies.find((c) => c.id === item.CompanyId)?.name;
                }

                return {
                    name: item.ShortName,
                    path: `/BuyerPortal/parking-lot/${item.Id}/${path?.path}`,
                    count: item.DocumentCount,
                    companyName: companyName,
                };
            })
            .filter((item): item is NonNullable<typeof item> => item !== undefined)
    );

    const groupsWithAllWorkflows = itemGroups.map((items) => {
        if (itemGroups.length < 2) {
            return items;
        }

        const companyTotal = items.reduce((acc, i) => acc + (i.count || 0), 0);

        if (includeSectionHeaders && items.length > 2) {
            // sectionHeader should always be the first item at this point
            const [sectionHeader, ...workflows] = items;

            return [
                sectionHeader,
                {
                    name: 'All Workflows',
                    path: `/BuyerPortal/parking-lot/erp/${sectionHeader.companyId}/all-workflows`,
                    count: companyTotal,
                    companyName: undefined,
                },
                ...workflows,
            ];
        }

        return items;
    });

    const allItems = groupsWithAllWorkflows.flat();

    if (allItems.length > 1 || !allItems.length) {
        allItems.unshift({
            name: 'All Workflows',
            path: '/BuyerPortal/parking-lot/all-workflows',
            count: totalAcrossCompanies,
            companyName: undefined,
        });
    }

    return allItems;
}

export const menuVisibilityMapper = ({
    menuSettings,
    isDashboardEnabled = false,
    dynamicDashboardMenus,
    forcedPathName,
    currentCompany,
    includeSectionHeaders = false,
    canViewSimDocuments = false,
}: {
    menuSettings: any;
    isDashboardEnabled?: boolean;
    dynamicDashboardMenus?: DashboardMenu[];
    forcedPathName?: string;
    currentCompany?: backendServices.ViewModels.CompanyViewModel;
    includeSectionHeaders?: boolean;
    canViewSimDocuments?: boolean;
}) => {
    const pathname = forcedPathName ? forcedPathName : window.location.pathname;

    // ! This is a temporary workaround to avoid the horizontal overflow when viewing a document while the left nav is displayed
    const lastSegment = pathname.split('/').at(-1);
    const lastSegmentIsDocId = typeof Number(lastSegment) === 'number' && !isNaN(Number(lastSegment));
    const isNewParkingLotDocumentCreator = !!subNavPaths['parking-lot'].find(
        (subItem) => lastSegment === subItem.path && subItem.showLeftNavInParkingLot === false
    );

    if (pathname.includes('dashboard') && isDashboardEnabled) {
        if (dynamicDashboardMenus && dynamicDashboardMenus.length > 0) {
            return dynamicDashboardMenus.map((menu) => getDashboardMenuNavItem(menu));
        }

        return subNavPaths.dashboard;
    }

    if (pathname.includes('transactions') && !lastSegmentIsDocId) {
        return subNavPaths.transactions.filter((item) => {
            if (!parseBusinessDocumentVisibility(menuSettings, item.docType)) {
                return false;
            }
            // SIM Documents require the dedicated permission on top of the company enable flag
            if (item.docType === BusinessDocType.SIMDocument) {
                return canViewSimDocuments;
            }
            return true;
        });
    }

    if (
        pathname.includes('parking-lot') &&
        !pathname.includes('settings') &&
        !lastSegmentIsDocId &&
        !isNewParkingLotDocumentCreator
    ) {
        const items = getParkingLotMenuItems({ currentCompany, menuSettings, includeSectionHeaders });
        return items;
    }

    if (pathname.includes('dynamic-discounting')) {
        return subNavPaths['dynamic-discounting'];
    }

    // We need to check for supplier portal because otherwise these items would be added when visiting `/BuyerPortal/parking-lot/${workflowId}/parked-documents/${documentId}`.
    // This will only happen on the parking lot workflow.
    if (pathname.includes('documents') && pathname.includes('SupplierPortal')) {
        return subNavPaths.documents.filter((item) => parseBusinessDocumentVisibility(menuSettings, item.docType));
    }

    return null;
};

export const disableLeftNavLink = (pathname: string) => {
    const patterns = [/^\/BuyerPortal\/transactions\/invoices\/\d+\/drilldown$/];

    return patterns.some((pattern) => pattern.test(pathname));
};
